'use client';
import { type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';
import { cn } from '../../lib/utils';
import { RAIL_MAX_WIDTH } from '../../lib/telemetryRail';

/** The rail's popover: a panel of rail rows.
 *
 *  The collapsed rail shows one instrument per section, and the sections that carry live work open a
 *  panel of their rows on a click. That panel is a POPOVER and not a menu: a row here is a thing with
 *  its own controls — a stop button, a mark that names its model, a name that reveals a brief — and a
 *  menu may only hold actions. The rows are the same `RailRow`s the open rail draws, so both densities
 *  read as one list.
 *
 *  The panel is the RAIL at its widest (`RAIL_MAX_WIDTH`, from the rail's own width contract): a live
 *  row carries a name, a model's mark, the run's note, its elapsed time, its speed and a stop control,
 *  and at the rail's default width the name itself would be cut. The panel never grows past the
 *  viewport, and its padding is the rail's own row rhythm: a row's hover wash bleeds 8px (`-mx-2`) past
 *  its text, so the panel pads 8px and the rows fill it exactly. Any less and the bleed overflows the
 *  scrolling panel sideways, which draws a horizontal scrollbar under the rows.
 *
 *  The trigger is the caller's own element (as a child of the trigger slot), so the instrument keeps
 *  its own shape, its own classes and its own accessible name; this component owns the panel, its
 *  placement and its dismissal. */
export function RailMenu({ trigger, children, open, onOpenChange, side = 'bottom', align = 'start', contentClassName = '', testId }: {
  /** The instrument that opens the panel. It must forward its props (a `Button` does). */
  trigger: ReactNode;
  /** The panel's rows — a `RailList` of `RailRow`s. */
  children: ReactNode;
  /** Controlled open state; omit it to let the panel own its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** The panel's own classes, beside the rail's own width and padding. */
  contentClassName?: string;
  testId?: string;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        data-testid={testId}
        onOpenAutoFocus={(event) => {
          // Focus the panel, not a child's tooltip anchor, until the user chooses a control.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={12}
        style={{ width: RAIL_MAX_WIDTH }}
        className={cn('max-w-[calc(100vw-var(--spacing)*8)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2', contentClassName)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
